import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button as BaseButton, Header3SemiBold, TextArea } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useArrayManagement from '@ubeya/shared-web/hooks/useArrayManagement';
import { useField, useForm } from 'react-final-form';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import { uuidv4 } from '@ubeya/shared/utils/uuid';
import { SORT_BY_STRING } from '@ubeya/shared/utils/sorting';
import LocationAreasTable from './LocationAreasTable';
import SubLocationModal from './SubLocationModal';

const Button = styled(BaseButton)`
  margin: 20px 0;
  padding: 0;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
`;

const TableSection = styled.div`
  margin-top: 20px;
`;

const Category = styled(Header3SemiBold)`
  background: ${({ theme }) => theme.colors.grey1};
  letter-spacing: 2.25px;
  color: ${({ theme }) => theme.colors.black};
  margin: 40px -10px 0px -10px;
  padding: 4px 13px;
`;

const SubLocationsSection = ({ setSubLocationsChanged }) => {
  const [selectedSubLocation, setSelectedSubLocation] = useState(null);
  const { t } = useContext(ConfigContext);
  const { data, handleAdd, handleAddItems, handleEdit, handleDelete } = useArrayManagement('subLocations');
  const { isUbeyaAdmin } = useUser();

  const visibleSubLocations = useMemo(() => data.filter(({ isDeleted }) => !isDeleted).sort(SORT_BY_STRING('name')), [
    data
  ]);

  const {
    input: { value: locationName }
  } = useField('name');

  const {
    input: { value: address }
  } = useField('address');

  const onButtonClick = useCallback(() => {
    setSelectedSubLocation({});
  }, [setSelectedSubLocation]);

  const allowAddSubLocation = !!address && !!locationName;

  const [superAdminAreasText, setSuperAdminAreasText] = useState('');

  /* tmp for super admins  */
  const form = useForm();

  const {
    values: { lat, lng, photo, image, location }
  } = form.getState();

  const subLocationsNamesSet = (data || []).reduce((output, current) => {
    if (current.name) {
      output.add(current.name);
    }
    return output;
  }, new Set());

  const handleChange = () => {
    setSuperAdminAreasText(superAdminAreasText);
    const newLines = superAdminAreasText.split('\n');
    const newLinesCleanArr = Array.from(new Set(newLines.filter((line) => !!line && !subLocationsNamesSet.has(line))));

    handleAddItems(
      newLinesCleanArr.map((line) => {
        return {
          id: uuidv4(),
          address,
          lat,
          lng,
          ...location,
          name: line,
          photo: photo || image
        };
      })
    );

    setSuperAdminAreasText('');
  };

  return (
    <>
      <Category>{t('subLocationsSectionAreasTitle', { locationName })}</Category>

      {visibleSubLocations.length > 0 && (
        <TableSection>
          <LocationAreasTable
            t={t}
            data={visibleSubLocations}
            onEdit={setSelectedSubLocation}
            onDelete={handleDelete}
          />
        </TableSection>
      )}

      <Actions>
        <Button type="button" narrow tertiary onClick={onButtonClick} disabled={!allowAddSubLocation}>
          {`+ ${t('addNewArea')}`}
        </Button>
      </Actions>

      {isUbeyaAdmin && allowAddSubLocation && (
        <>
          <div>Add areas separated by a new line (Super admins only) </div>
          <TextArea value={superAdminAreasText} onChange={setSuperAdminAreasText} minRows={1} />
          <Button type="button" narrow tertiary onClick={handleChange}>
            apply areas
          </Button>
        </>
      )}

      {selectedSubLocation && (
        <SubLocationModal
          setSubLocationsChanged={setSubLocationsChanged}
          handleAdd={handleAdd}
          handleEdit={handleEdit}
          selectedSubLocation={selectedSubLocation}
          t={t}
          onClose={() => setSelectedSubLocation(false)}
        />
      )}
    </>
  );
};

export default SubLocationsSection;
